import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useGlobalContext } from '../../context';
import UserSideNav from '../components/UserSideNav';
import api from '../../api';
import { GiHamburgerMenu } from "react-icons/gi";
import { BiLogOut, BiSolidWallet } from "react-icons/bi";
import userDashLinks from "./userDashLinks";

const UserProfile = () => {
  const { allUserMongoData } = useGlobalContext();

  const [navActive, updateNavActive] = useState(null);

  useEffect(() => {
    if (navActive) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [navActive]);

  const clearSess = async () => {
    try { await api.post('/api/logout'); } catch (e) {}
    window.sessionStorage.clear();
  };

  const hideNav = () => {
    updateNavActive(false);
  };

  const [formValues, upFormValues] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });

  // seed form with live data once available
  useEffect(() => {
    if (allUserMongoData) {
      upFormValues({
        name: allUserMongoData.username || '',
        email: allUserMongoData.email || '',
        phone: allUserMongoData.phone || '',
        address: allUserMongoData.address || ''
      });
    }
  }, [allUserMongoData]);

  const [isLoading, setIsLoading] = useState(false);

  const submitProfileForm = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const profilePost = await api.post('/api/user/profile', { ...formValues });
      const profileData = profilePost.data;

      if (profileData.errMsg) {
        toast.error(profileData.errMsg);
      } else {
        toast.success(profileData.successMsg);
        setTimeout(() => {
          window.location.reload();
        }, 4000);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const initials = allUserMongoData?.username
    ? allUserMongoData.username.charAt(0).toUpperCase()
    : '?';

  return (
    <div className='absolute overflow-hidden w-full min-h-full text-white bg-black'>
      <UserSideNav
        hideNav={hideNav}
        logout='/signin'
        logoutBtn={clearSess}
        translateAction={navActive ? `translate-x-[0px]` : `translate-x-[-500px]`}
        clearSess={clearSess}
        backgroundColor='bg-black'
        arrowCoLor='text-white'
        iconBorderColor='border-white'
        iconBgColor='bg-inherit'
        textColor='text-white'
        links={userDashLinks}
        logouticon={<BiLogOut className='cursor-pointer text-[20px] text-white transition-all ease-in duration-300 hover:scale-[1.1]' />}
      />

      {navActive && (
        <div className="fixed inset-0 bg-black/60 z-40 xl:hidden" onClick={hideNav} />
      )}

      <div className='relative w-full h-full p-4 overflow-hidden mb-[100px] xl:pl-[304px] xl:pr-6'>
        {/* top nav */}
        <nav className="relative w-full min-h-[100px] flex justify-center items-start">
          <div className="relative w-full min-h-[50px] flex justify-between items-center xl:justify-end">
            <button onClick={() => updateNavActive(true)} className="relative xl:hidden">
              <GiHamburgerMenu className='text-[20px] text-[#A9ABAD]' />
            </button>

            <div className="relative flex justify-center items-center space-x-2 min-h-[38px] py-2 px-4 bg-[#181C26] border border-[#1f2535] rounded-lg transition-all ease-in duration-200 hover:border-[#473ECD]">
              <BiSolidWallet className='text-[14px] text-[#A9ABAD]' />
              <p className='text-[12px] text-[#A9ABAD]'>
                ${allUserMongoData?.balance ? allUserMongoData.balance.toLocaleString() : '0'}
              </p>
            </div>
          </div>
        </nav>

        {/* page header */}
        <div className="relative w-full flex flex-col justify-start items-start space-y-1 pt-4 pb-8">
          <h3 className="text-[20px] font-[600] text-white">Profile</h3>
          <p className="text-[#A9ABAD] text-[14px]">Manage your personal information.</p>
        </div>

        <div className="w-full flex flex-col space-y-4 sm:w-[480px]">
          {/* avatar + name header card */}
          <div className="w-full bg-[#181C26] border border-[#1f2535] rounded-xl p-6 flex items-center space-x-4">
            <div className="w-[52px] h-[52px] rounded-full bg-userDashPurple flex justify-center items-center flex-shrink-0">
              <span className="text-[20px] font-[600] text-white">{initials}</span>
            </div>
            <div>
              <p className="text-[15px] font-[600] text-white">
                {allUserMongoData?.username || '—'}
              </p>
              <p className="text-[12px] text-[#A9ABAD] mt-[2px]">
                {allUserMongoData?.email || '—'}
              </p>
            </div>
          </div>

          {/* editable form card */}
          <form onSubmit={submitProfileForm}>
            <div className="w-full bg-[#181C26] border border-[#1f2535] rounded-xl p-6 flex flex-col">

              {/* Name */}
              <div className="w-full flex flex-col space-y-2 pb-5">
                <label className="text-[11px] text-[#A9ABAD] uppercase tracking-widest">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formValues.name}
                  onChange={(e) => upFormValues({ ...formValues, name: e.target.value })}
                  className="w-full h-[44px] bg-[#0d1017] border border-[#1f2535] rounded-lg px-4 text-[16px] text-white focus:outline-none focus:border-[#473ECD] transition-colors"
                />
              </div>

              <div className="w-full h-[1px] bg-[#1f2535] mb-5" />

              {/* Email */}
              <div className="w-full flex flex-col space-y-2 pb-5">
                <label className="text-[11px] text-[#A9ABAD] uppercase tracking-widest">
                  Email Address
                </label>
                <input
                  type="text"
                  name="email"
                  value={formValues.email}
                  onChange={(e) => upFormValues({ ...formValues, email: e.target.value })}
                  className="w-full h-[44px] bg-[#0d1017] border border-[#1f2535] rounded-lg px-4 text-[16px] text-white focus:outline-none focus:border-[#473ECD] transition-colors"
                />
              </div>

              <div className="w-full h-[1px] bg-[#1f2535] mb-5" />

              {/* Phone */}
              <div className="w-full flex flex-col space-y-2 pb-5">
                <label className="text-[11px] text-[#A9ABAD] uppercase tracking-widest">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formValues.phone}
                  onChange={(e) => upFormValues({ ...formValues, phone: e.target.value })}
                  className="w-full h-[44px] bg-[#0d1017] border border-[#1f2535] rounded-lg px-4 text-[16px] text-white focus:outline-none focus:border-[#473ECD] transition-colors"
                />
              </div>

              <div className="w-full h-[1px] bg-[#1f2535] mb-5" />

              {/* Address */}
              <div className="w-full flex flex-col space-y-2">
                <label className="text-[11px] text-[#A9ABAD] uppercase tracking-widest">
                  Address
                </label>
                <input
                  type="text"
                  name="address"
                  value={formValues.address}
                  onChange={(e) => upFormValues({ ...formValues, address: e.target.value })}
                  className="w-full h-[44px] bg-[#0d1017] border border-[#1f2535] rounded-lg px-4 text-[16px] text-white focus:outline-none focus:border-[#473ECD] transition-colors"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full min-h-[44px] mt-4 flex justify-center items-center text-[14px] font-[500] bg-userDashPurple rounded-lg transition-all ease-in duration-200 hover:bg-[#3d34b5] disabled:opacity-60"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
